import authService from './authService';

let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    const response = await fetch(`${publicUrl}/config.json`);
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error('Failed to load config.json. Using default .env API URL', error);
  }
}

loadConfig();

export interface AssetListItem {
  id: string;
  assetName: string;
  assetType?: { name: string; fields?: string | any[] };
  owner?: { name: string };
  custodian?: { name: string };
  posture?: string;
  compliance?: string;
  date?: string;
}

export interface AssetDetail extends AssetListItem {
  fields?: Record<string, any> | string;
  relatedRisks?: any[];
  relatedProcesses?: any[];
  controls?: any[];
}

export interface AssetType {
  id: string;
  name: string;
}

interface FetchAssetsParams {
  page?: number;
  perPage?: number;
  query?: string;
}

export async function fetchAssetsApi(
  params: FetchAssetsParams = {},
): Promise<{ data: AssetListItem[]; totalPages: number; total: number }> {
  const { page = 1, perPage = 10, query = '' } = params;

  // Backend expects: page, limit, search (not pageNo, perPage, query)
  const url = `${BASE_URL}/assets?page=${page}&limit=${perPage}${
    query ? `&search=${encodeURIComponent(query)}` : ''
  }`;

  console.log('Fetching assets with URL:', url);
  console.log('Search query:', query);

  try {
    const response = await authService.apiCall(url, { method: 'GET' });
    const data = await response.json();
    
    console.log('Assets API response:', data);

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch assets');
    }

    return {
      data: data.data || [],
      totalPages: data.pagination?.totalPages || 1,
      total: data.pagination?.total || (data.data ? data.data.length : 0),
    };
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch assets');
  }
}

export async function fetchAssetTypesApi(): Promise<AssetType[]> {
  const url = `${BASE_URL}/asset-types?page=1&limit=50`;

  try {
    const response = await authService.apiCall(url, { method: 'GET' });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch asset types');
    }

    // Backend may return { data: { assetTypes: [...] } } or { data: [...] }
    return (data.data?.assetTypes || data.data || []) as AssetType[];
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch asset types');
  }
}

export async function fetchAssetByIdApi(id: string): Promise<AssetDetail> {
  const url = `${BASE_URL}/assets/${id}`;

  try {
    const response = await authService.apiCall(url, { method: 'GET' });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch asset details');
    }

    return data.data as AssetDetail;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch asset details');
  }
}

export interface CreateAssetPayload {
  assetName: string;
  assetTypeId: string;
  ownerId: string;
  custodianId: string;
  date: string;
  posture?: 'High' | 'Medium' | 'Low' | null;
  compliance?: string | null;
  fields?: Record<string, any>;
}

export async function createAssetApi(payload: CreateAssetPayload): Promise<void> {
  const url = `${BASE_URL}/assets`;

  try {
    const response = await authService.apiCall(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create asset');
    }
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to create asset');
  }
}

export async function updateAssetApi(id: string, payload: CreateAssetPayload): Promise<void> {
  const url = `${BASE_URL}/assets/${id}`;

  try {
    const response = await authService.apiCall(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update asset');
    }
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to update asset');
  }
}

export async function deleteAssetApi(id: string): Promise<void> {
  const url = `${BASE_URL}/assets/${id}`;

  try {
    const response = await authService.apiCall(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status === 204) {
      return;
    }

    const text = await response.text();
    if (!text) {
      if (response.ok) return;
      throw new Error('Failed to delete asset');
    }

    const data = JSON.parse(text);
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete asset');
    }
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to delete asset');
  }
}

export async function addControlsToAssetApi(
  assetId: string,
  payload: { controlIds: string[] },
): Promise<{ success: boolean; message?: string }> {
  const url = `${BASE_URL}/assets/${assetId}/controls`;

  try {
    const response = await authService.apiCall(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to add controls to asset');
    }

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to add controls to asset');
  }
}

export async function removeControlsFromAssetApi(
  assetId: string,
  payload: { controlIds: string[] },
): Promise<{ success: boolean; message?: string }> {
  const url = `${BASE_URL}/assets/${assetId}/controls`;

  try {
    const response = await authService.apiCall(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to remove controls from asset');
    }

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to remove controls from asset');
  }
}

export interface BulkAssetWithNames {
  assetName: string;
  assetTypeName: string;
  assetTypeFields: Array<{
    label: string;
    type: string;
    required: boolean;
  }>;
  ownerName: string;
  custodianName: string;
  posture: string;
  compliance: string;
  date: string;
  fields: Record<string, any>;
}

export interface BulkCreateAssetsWithNamesPayload {
  assets: BulkAssetWithNames[];
}

export async function createBulkAssetsWithNamesApi(
  payload: BulkCreateAssetsWithNamesPayload,
): Promise<{ success: boolean; message?: string }> {
  const url = `${BASE_URL}/assets/bulk-with-names`;

  try {
    const response = await authService.apiCall(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create bulk assets');
    }

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to create bulk assets');
  }
}


